"use client";

import { events } from "@/common/constants";
import { misc, util } from "@/common/theme";
import CustomSplitText from "@/mono/components/CustomSplitText";
import { TransitionLink } from "@/mono/components/TransitionLink";
import emitter from "@/mono/lib/emitter";
import { useTicker } from "@/mono/lib/hooks/useRaf";
import { useScrollRigScrollTrigger } from "@/mono/lib/hooks/useScrollRigScrollTrigger";
import useSubscribe from "@/mono/lib/hooks/useSubscribe";
import { lerp } from "@/mono/lib/math";
import { mediaTabletDown as mediaMobile } from "@/mono/lib/responsive";
import { easeInExpo } from "@/mono/lib/styles/easing";
import { setTypography } from "@/mono/lib/styles/mixins";
import { useGlobalStore } from "@/mono/store/global";
import {
  ScrollScene,
  UseCanvas,
} from "@/mono/third-party/r3f-scroll-rig-custom";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { create } from "zustand";
import { ScrollingText } from "../../ScrollingText";
import { SectionTitle } from "../../SectionTitle";
import NineGLImage from "../../gl/NineGLImage";
import WhatWeDoServicesMobile from "./WhatWeDoServicesMobile";

import { serviceMap } from "@/common/constants";
import useEvent from "@/mono/lib/hooks/useEvent";
import useResize from "@/mono/lib/hooks/useResize";
import { withAssets } from "@/mono/store/assets";
import { mediaDesktop } from "mono/lib/responsive";
import { CanvasTexture } from "three";

export const serviceStore = create(() => ({
  currIndex: 0,
  prevIndex: 0,
}));

const WHAT_WE_DO_POINTER_LEAVE = "WhatWeDo:pointer_leave";

const SERVICE_IMAGE_URLS = {
  campaign: "/images/services/campaign.webp",
  identity: "/images/services/identity.webp",
  webdesign: "/images/services/web-design.webp",
  "photo-video": "/images/services/photo-video.webp",
  strategy: "/images/services/strategy.webp",
};

function ServicesDOMImage({ serviceImages }) {
  const imgRef = useRef();
  const serviceKeys = Object.keys(serviceMap);

  useSubscribe(serviceStore, ({ currIndex }) => {
    if (!imgRef.current) return;
    const key = serviceKeys[currIndex]?.toLowerCase();
    const url = SERVICE_IMAGE_URLS[key];
    if (url) imgRef.current.src = url;
  });

  const initialKey = serviceKeys[0]?.toLowerCase();

  return (
    <div style={{ width: "100%", height: "100%", overflow: "hidden", borderRadius: "10px", position: "relative" }}>
      <img
        ref={imgRef}
        src={SERVICE_IMAGE_URLS[initialKey]}
        alt=""
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
    </div>
  );
}

function ServicesDesktop({ serviceImages }) {
  const track = useRef();
  const noWebGLImages = useGlobalStore((s) => s.noWebGLImages);

  const indicator = useRef();
  const _pos = useRef(0);
  const yPositions = useRef([]);

  const services = Object.values(serviceMap);
  const textsLeft = useRef([]);
  const textsRight = useRef([]);

  const list = useRef();
  const glImage = useRef();
  const items = useRef([]);

  const gap = useRef(0);

  const getUrl = (i) => `/service/${Object.keys(serviceMap)[i].toLowerCase()}`;

  const [currentURL, setCurrentURL] = useState(getUrl(0));

  const { start, stop } = useTicker(
    ({ delta }) => {
      if (!indicator.current) return;

      const pos = gap.current * serviceStore.getState().currIndex;

      _pos.current = lerp(_pos.current, pos, 0.05, delta, 60);

      indicator.current.style.transform = `translateY(${_pos.current}px)`;
    },
    { initiallyActive: true }
  );

  useScrollRigScrollTrigger({
    el: track,
    handleIn: () => {
      start();
      if (!noWebGLImages) glImage.current?.animateIn();

      textsLeft.current[0]?.animateIn();
      textsRight.current[0]?.animateIn({ delay: 0.3 });
    },
    handleOut: () => {
      stop();
    },
  });

  useSubscribe(serviceStore, ({ currIndex, prevIndex }) => {
    setCurrentURL(getUrl(currIndex));
  });

  useResize(
    () => {
      if (!items.current.length) return;

      indicator.current = items.current?.[0]?.indicator;

      if (!indicator.current) return;

      const itemElements = items.current.map((item) => item.el);
      const firstItem = itemElements[0];

      const computedStyle = window.getComputedStyle(firstItem);
      const itemHeight = firstItem.offsetHeight;
      const marginBottom = parseInt(computedStyle.marginBottom);
      gap.current = itemHeight + marginBottom;
    },
    {
      immediate: true,
      debounce: 100,
    },
    [indicator.current, items.current, items.current.length]
  );

  return (
    <div className="what-we-do__bottom">
      <div className="what-we-do__list" ref={list}>
        <ul
          onMouseLeave={() => {
            emitter.emit(WHAT_WE_DO_POINTER_LEAVE);
          }}
        >
          {services.map((service, i) => {
            return (
              <Service
                ref={(el) => (items.current[i] = el)}
                key={i.toString()}
                i={i}
                getUrl={getUrl}
              >
                {service}
              </Service>
            );
          })}
        </ul>
      </div>
      {noWebGLImages ? (
        <TransitionLink href={currentURL}>
          <article ref={track}>
            <ServicesDOMImage serviceImages={serviceImages} />
          </article>
        </TransitionLink>
      ) : (
        <>
          <TransitionLink href={currentURL}>
            <article ref={track}></article>
          </TransitionLink>
          <UseCanvas>
            <ScrollScene track={track}>
              {(props) => (
                <ServicesGLImage
                  getUrl={getUrl}
                  track={track}
                  serviceImages={serviceImages}
                  ref={glImage}
                  {...props}
                />
              )}
            </ScrollScene>
          </UseCanvas>
        </>
      )}
    </div>
  );
}

const Service = forwardRef(({ children, i, getUrl, onLeave }, ref) => {
  const el = useRef();
  const indicator = useRef();
  const isActive = useRef(i === 0);

  function handleEnter() {
    const { currIndex } = serviceStore.getState();
    serviceStore.setState({ currIndex: i, prevIndex: currIndex });

    // TEMP: emitter.emit(events.CURSOR_SHOW, { text: `Go to ${children} page` });
    document.body.style.cursor = "pointer";
  }

  function handleLeave() {
    document.body.style.cursor = "default";
    emitter.emit(events.CURSOR_HIDE);
    onLeave?.();
  }

  useSubscribe(serviceStore, ({ currIndex }) => {
    if (currIndex === i && !isActive.current) {
      isActive.current = true;

      el.current?.classList.add("active");
    } else if (currIndex !== i && isActive.current) {
      isActive.current = false;
      el.current?.classList.remove("active");
    }
  });

  useImperativeHandle(ref, () => ({
    indicator: indicator.current,
    el: el.current,
  }));

  return (
    <li
      ref={el}
      className={`${i === 0 ? "active" : ""}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {i === 0 && <div ref={indicator} className="list-indicator" />}
      <TransitionLink href={getUrl(i)}>{children}</TransitionLink>
    </li>
  );
});

export const ServicesGLImage = forwardRef(
  ({ track, getUrl, serviceImages, ...props }, ref) => {
    const localRef = useRef();

    const [initialTexture, setInitialTexture] = useState(null);

    // Need some kind of asset service to manage creation etc
    const textures = useMemo(() => {
      const mappedArray = serviceImages.map(({ name, asset }) => {
        return {
          service: name.replace("service", "").toLowerCase(),
          texture: new CanvasTexture(asset),
        };
      });

      const identity = mappedArray.find((t) => t.service === "identity");
      if (identity) setInitialTexture(identity.texture);

      return mappedArray;
    }, [serviceImages]);

    useEffect(() => {
      return () => {
        textures.forEach((t) => t.texture.dispose());
      };
    }, [textures]);

    useImperativeHandle(ref, () => localRef.current);

    useSubscribe(
      serviceStore,
      async ({ currIndex }) => {
        localRef.current?.triggerPointerOver();

        // Yikes..
        const texture = textures.find(
          (t) =>
            t.service ===
            Object.keys(serviceMap)[currIndex].replace("-", "").toLowerCase()
        );

        if (!texture) return;

        localRef.current?.shaderObject.set("tMap", texture.texture);
      },
      [textures]
    );

    useEvent(WHAT_WE_DO_POINTER_LEAVE, () => {
      localRef.current?.triggerPointerOut();
    });

    if (!initialTexture) return null;

    return (
      <NineGLImage
        hoverText={"Go to service"}
        tMap={initialTexture}
        onLeave={() => localRef.current?.triggerPointerOut()}
        {...props}
        ref={localRef}
      />
    );
  }
);

export const HomeWhatWeDo = withAssets([
  {
    type: "image",
    pattern: "service",
    groupName: "serviceImages",
    expectedCount: Object.keys(serviceMap).length,
  },
])(({ serviceImages, ...props }) => {
  const isMobileLayout = useGlobalStore(
    (state) => state.isTabletOrSmallerLayout
  );

  useEffect(() => {
    serviceStore.setState({
      currIndex: 0,
      prevIndex: 0,
    });
  }, []);

  return (
    <>
      <SectionTitle>What we do</SectionTitle>

      <Wrapper>
        <div className="what-we-do__top">
          <ScrollingText>You got the vibe, we got the tools.</ScrollingText>
          <CustomSplitText tag="p">
            Opvallen is niet genoeg. Wij zorgen ervoor dat jouw merk onthouden
            wordt. Voorbij de hype, recht het hart in. Alleen zo blijf je top of
            mind.
          </CustomSplitText>
        </div>
        {isMobileLayout ? (
          <WhatWeDoServicesMobile serviceImages={serviceImages} />
        ) : (
          <ServicesDesktop serviceImages={serviceImages} />
        )}
      </Wrapper>
    </>
  );
});

const Wrapper = styled.section`
  position: relative;
  width: 100%;

  ${util.inner}

  .scrolling-text {
    max-width: 1350rem;
  }

  ${mediaMobile(`
    .scrolling-text {
      max-width: 100%;
    }
  `)}

  .what-we-do__top {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    align-items: flex-start;
    position: relative;

    p {
      position: absolute;
      left: 50%;
      max-width: 1200rem;
    }

    ${mediaMobile(`
        flex-flow: column nowrap;
        p {
          position: relative !important;
          top: unset !important;
          left: unset !important;
          display: block;
          max-width: 100%;
          margin: 50rem 0rem !important;
        }
    `)}
  }

  .what-we-do__bottom {
    margin-top: 200rem;
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    position: relative;

    ${mediaMobile(`
      margin-top: 100rem;
      display: flex;
      flex-flow: row nowrap;
    `)}
  }

  --indicatorSize: 40rem;

  ul {
    padding-left: 280rem;

    ${mediaMobile(`
      padding-left: 80rem;
    `)}

    li:first-of-type {
      a {
        right: var(--indicatorSize);
      }
    }

    li {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;

      white-space: nowrap;

      margin-bottom: 65rem;

      a {
        position: relative;
      }

      ${({ theme: { locale } }) => setTypography("li", locale)};
      color: ${({ theme }) => theme.theme.inactiveTextColor};
      transition: color 0.2s ${easeInExpo};

      &.active {
        color: ${({ theme }) => theme.theme.textColor};
      }
    }
  }

  .what-we-do__list {
    position: relative;

    ${mediaMobile(`
      width: 50%;
    `)}
  }

  .list-indicator {
    right: 240rem;
    position: relative;

    width: var(--indicatorSize);
    height: var(--indicatorSize);
    background: ${({ theme }) => theme.theme.textColor};
    border-radius: ${misc.borderRadius};
  }

  ${mediaDesktop(`
    a:not(li a) {
      display: block;
      width: 65%;
      aspect-ratio: 16/9;
    }
  `)}
  article {
    width: 100%;
    height: 100%;

    ${mediaMobile(`
      width: 50%;
      height: auto;
      top: 0;
    `)}

    aside {
      position: absolute;
      top: 70%;
      left: -50rem;

      display: flex;
      gap: 50rem;

      .what-we-do__shuffled-text {
        max-width: 400rem;
        .txt-shuffle {
          color: ${({ theme }) => theme.theme.accent};
        }
      }
    }

    ${mediaMobile(`
      position: relative;
    `)}
  }
`;
